import React, { useCallback, useEffect, useMemo } from "react";
import * as Scrivito from "scrivito";
import { camelCase } from "lodash-es";
import { handleProjectListFiltersUpdate, updateProjectFilters } from "utils";
import cx from "classnames";
import { useTranslate } from "hooks";
import { FilterBtn } from "./FilterBtn";
import { useProjectListContext } from "../../hooks/useProjectListContext";

export const ProjectListFilters = Scrivito.connect(({ className }) => {
  const translate = useTranslate();
  const {
    overallStatistics,
    projectsCount,
    setSearchQuery,
    setSharedStatusSwitcher,
    projectStatuses,
    setFilterStatuses,
    handleSetCurrentPage,
    filters,
    setFilters,
    statistics,
  } = useProjectListContext();

  const translations = useMemo(
    () => ({
      shared: translate("SHARED"),
      notShared: translate("NOT_SHARED"),
      allProjects: translate("ALL_PROJECTS"),
    }),
    [translate],
  );

  useEffect(() => {
    const newFilters = updateProjectFilters(
      translations,
      overallStatistics,
      projectsCount,
      setSearchQuery,
      setSharedStatusSwitcher,
      setFilters,
      projectStatuses,
    );
    setFilters((prevFilters) =>
      prevFilters.length <= 1 ? newFilters : prevFilters,
    );
  }, [
    translations,
    overallStatistics,
    projectsCount,
    setSearchQuery,
    setSharedStatusSwitcher,
    setFilters,
    projectStatuses,
  ]);

  useEffect(() => {
    setFilterStatuses(
      filters?.reduce((newStatuses, filter) => {
        if (filter.isStatusFilter && filter.isActive) {
          newStatuses.push(filter.name);
        }
        return newStatuses;
      }, []),
    );
  }, [filters, setFilterStatuses]);

  useEffect(() => {
    if (!statistics && !overallStatistics) {
      return;
    }

    setFilters((prevFilters) =>
      prevFilters?.map((fl) => {
        let count = 0;
        const flCamelCase = camelCase(fl.id);

        if (flCamelCase === "allProjects" && projectsCount) {
          count = projectsCount;
        }
        if (flCamelCase === "shared" || flCamelCase === "notShared") {
          count = overallStatistics?.[flCamelCase];
        }

        if (statistics[flCamelCase]) {
          count = statistics[flCamelCase];
        } else if (overallStatistics?.statuses?.[fl.id]) {
          count = overallStatistics?.statuses[fl.id];
        }

        return {
          ...fl,
          count,
        };
      }),
    );
  }, [statistics, overallStatistics, projectsCount, setFilters]);

  const handleFilterActivation = useCallback(
    (id) => {
      setFilters((prevFilters) =>
        handleProjectListFiltersUpdate(prevFilters, { id }),
      );
      handleSetCurrentPage();
    },
    [setFilters, handleSetCurrentPage],
  );

  return (
    <div className={cx("d-flex flex-wrap gap-2", className)}>
      {filters.map(
        ({ id, name, count, color, icon, isActive, disabled, onClick }) => (
          <FilterBtn
            key={id}
            id={id}
            name={name}
            count={count || 0}
            color={color}
            icon={icon}
            isActive={isActive}
            onClick={onClick ?? handleFilterActivation}
            disabled={disabled}
          />
        ),
      )}
    </div>
  );
});
